import axios from 'axios';
import { API_URL } from './apiConfig';

// Cache for organization date/time settings
let dateTimeCache = {
  dateFormat: 'DD/MM/YYYY',
  timeFormat: '12h',
  timezone: 'Asia/Kolkata',
  timestamp: 0,
  cacheDuration: 1 * 60 * 1000 // 1 minute
};

/**
 * Load organization date/time settings from API or cache
 */
export const getOrganizationDateTimeSettings = async (forceRefresh = false) => {
  const now = Date.now();

  if (
    !forceRefresh &&
    dateTimeCache.timestamp &&
    now - dateTimeCache.timestamp < dateTimeCache.cacheDuration
  ) {
    return {
      dateFormat: dateTimeCache.dateFormat,
      timeFormat: dateTimeCache.timeFormat,
      timezone: dateTimeCache.timezone
    };
  }

  try {
    const token = localStorage.getItem('token');
    if (!token) {
      return {
        dateFormat: dateTimeCache.dateFormat,
        timeFormat: dateTimeCache.timeFormat,
        timezone: dateTimeCache.timezone
      };
    }

    const response = await axios.get(`${API_URL}/organizations/my`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });

    if (response.data.success && response.data.data?.settings) {
      const s = response.data.data.settings;
      dateTimeCache = {
        dateFormat: s.dateFormat || 'DD/MM/YYYY',
        timeFormat: s.timeFormat || '12h',
        timezone: s.timezone || 'Asia/Kolkata',
        timestamp: now,
        cacheDuration: dateTimeCache.cacheDuration
      };
    }
  } catch (err) {
    console.error('Error fetching organization date/time settings:', err);
  }

  return {
    dateFormat: dateTimeCache.dateFormat,
    timeFormat: dateTimeCache.timeFormat,
    timezone: dateTimeCache.timezone
  };
};

/**
 * Build Intl.DateTimeFormat options from settings
 */
const buildDateOptions = (dateFormat, timezone) => {
  let options = { timeZone: timezone, year: 'numeric', month: 'short', day: 'numeric' };

  switch (dateFormat) {
    case 'MM/DD/YYYY':
      options = { ...options, month: '2-digit', day: '2-digit' };
      break;
    case 'YYYY-MM-DD':
      options = { ...options, month: '2-digit', day: '2-digit' };
      break;
    case 'DD/MM/YYYY':
    default:
      options = { ...options, month: '2-digit', day: '2-digit' };
      break;
  }

  return options;
};

const buildDateTimeOptions = (dateFormat, timeFormat, timezone) => {
  const base = buildDateOptions(dateFormat, timezone);
  const hour12 = timeFormat === '12h';

  return {
    ...base,
    hour: '2-digit',
    minute: '2-digit',
    hour12
  };
};

/**
 * Format date using organization settings (async)
 */
export const formatDateLocalized = async (value) => {
  if (!value) return '';
  const { dateFormat, timezone } = await getOrganizationDateTimeSettings();
  try {
    const dt = new Date(value);
    const options = buildDateOptions(dateFormat, timezone);
    return new Intl.DateTimeFormat(undefined, options).format(dt);
  } catch (err) {
    console.error('Error formatting date (async):', err);
    return new Date(value).toLocaleDateString();
  }
};

/**
 * Format date/time using organization settings (async)
 */
export const formatDateTimeLocalized = async (value) => {
  if (!value) return '';
  const { dateFormat, timeFormat, timezone } = await getOrganizationDateTimeSettings();
  try {
    const dt = new Date(value);
    const options = buildDateTimeOptions(dateFormat, timeFormat, timezone);
    return new Intl.DateTimeFormat(undefined, options).format(dt);
  } catch (err) {
    console.error('Error formatting datetime (async):', err);
    return new Date(value).toLocaleString();
  }
};

/**
 * Synchronous date formatting using cached settings
 * (useful in render functions where async is inconvenient)
 */
export const formatDateLocalizedSync = (value) => {
  if (!value) return '';
  const { dateFormat, timezone } = dateTimeCache;
  try {
    const dt = new Date(value);
    const options = buildDateOptions(dateFormat, timezone);
    return new Intl.DateTimeFormat(undefined, options).format(dt);
  } catch (err) {
    console.error('Error formatting date (sync):', err);
    return new Date(value).toLocaleDateString();
  }
};

export const formatDateTimeLocalizedSync = (value) => {
  if (!value) return '';
  const { dateFormat, timeFormat, timezone } = dateTimeCache;
  try {
    const dt = new Date(value);
    const options = buildDateTimeOptions(dateFormat, timeFormat, timezone);
    return new Intl.DateTimeFormat(undefined, options).format(dt);
  } catch (err) {
    console.error('Error formatting datetime (sync):', err);
    return new Date(value).toLocaleString();
  }
};

